import { Dialog, Stack, Inline, Button, vars, Text } from '@etg/wings';
import { useTranslation } from '@eti/providers';
import { Check } from '@phosphor-icons/react';
import { Icon, Notification, variants } from '@eti/components';
import { css } from '@eti/styles';

const illustrationStyles = css`
  background-color: ${vars.colors.promotional.light};
  border-radius: 50%;
`;

const listStyles = css`
  list-style: none;
  padding-left: 0;
`;

const listItemStyles = css`
  display: flex;
  flex-direction: column;
  padding-bottom: 16px;

  &:last-child {
    padding-bottom: 0;
  }
`;

const descriptionTextStyles = css`
  display: block;
  padding-left: 38px;
`;

interface SelfTransferModalProps {
  isOpen: boolean;
  setIsOpen: (arg: boolean) => void;
}

const SelfTransferModal = ({ isOpen, setIsOpen }: SelfTransferModalProps) => {
  const { t } = useTranslation();
  const listItems = [...Array(3)].map((_, index) => ({
    title: t(`Result.Page.SelfTransferDealsModal.ListItem${index + 1}.Title`),
    description: t(`Result.Page.SelfTransferDealsModal.ListItem${index + 1}.Description`),
  }));

  return (
    <Dialog
      closeButtonAriaLabel={t('ScreenReader.CloseButtonDialog.AriaLabel.Text')}
      data-testid="result-page-self-transfer-modal"
      illustration={
        <Icon
          className={illustrationStyles}
          color={vars.colors.promotional.dark}
          id="self-transfer"
          size="40px"
        />
      }
      isOpen={isOpen}
      onDismiss={() => setIsOpen(false)}
      title={t('Result.Page.SelfTransferDealsModal.Title')}
    >
      <Stack spacing={24}>
        <Stack>
          <Text>{t('Result.Page.SelfTransferDealsModal.Description')}</Text>
          <ul className={listStyles}>
            {listItems.map(({ title, description }, index) => (
              <li className={listItemStyles} key={index}>
                <Inline>
                  <Check
                    aria-hidden
                    color={vars.colors.signal.positive.light}
                    size="20"
                    weight="bold"
                  />
                  <Text>
                    <strong>{title}</strong>
                  </Text>
                </Inline>

                <Text asHtml={index === 2} className={descriptionTextStyles}>
                  {description}
                </Text>
              </li>
            ))}
          </ul>
          <Notification variant={variants.INFORMATION}>
            {t('Result.Page.SelfTransferDealsModal.Notification.Text')}
          </Notification>
        </Stack>
        <Inline align="end">
          <Button onClick={() => setIsOpen(false)} variant="primary">
            {t('Result.Page.SelfTransferModal.Button.Text')}
          </Button>
        </Inline>
      </Stack>
    </Dialog>
  );
};

export default SelfTransferModal;
